import { useMemo, useState, useEffect } from 'react';
import { X, CalendarDays, User, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'react-day-picker/locale';
import { cn } from '@/lib/utils';
import { isoToZoneParts, localTzToUTC } from './timeZoneHelpers';
import { timezoneToCountryCode, countryCodeToName } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';

interface RawSlot {
  id: string;
  slot: string; // ISO datetime
}

export function RescheduleModal({
  isOpen,
  onClose,
  currentSession,
  slots,
  therapistTimezone,
  patientTimezone,
  sessionDuration,
  selectedSlotId,
  onSelectSlot,
  onConfirm,
  confirming,
  loading,
  error,
  patientCountryCode,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentSession: { date: string; patient: string };
  slots: RawSlot[];
  therapistTimezone: string; // IANA e.g. 'America/Argentina/Buenos_Aires'
  patientTimezone?: string; // IANA e.g. 'America/Lima'
  sessionDuration: number; // minutes
  selectedSlotId: string | null;
  onSelectSlot: (slotId: string | null) => void;
  onConfirm?: () => void;
  confirming?: boolean;
  loading?: boolean;
  error?: string | null;
  patientCountryCode?: string | null;
}) {
  // Compute which dates have available slots
  const availableDates = useMemo(() => {
    const dateMap = new Map<string, Date>();
    slots.forEach(s => {
      const d = new Date(s.slot);
      const dateStr = d.toLocaleDateString('en-CA', { timeZone: therapistTimezone });
      if (!dateMap.has(dateStr)) {
        const [y, m, day] = dateStr.split('-').map(Number);
        dateMap.set(dateStr, new Date(y!, (m || 1) - 1, day));
      }
    });
    return Array.from(dateMap.values()).sort((a, b) => a.getTime() - b.getTime());
  }, [slots, therapistTimezone]);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // Auto-select first available date
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear slot selection when date changes
  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    onSelectSlot(null);
  };

  // País de la psicóloga derivado de su huso — antes la lista de slots decía "(Argentina)"
  // fijo, y le mentía a cualquier psicóloga que atienda desde otro país.
  const therapistIso2 = timezoneToCountryCode(therapistTimezone);
  const therapistCountry = countryCodeToName(therapistIso2) || 'Argentina';

  // Patient timezone label (city name) + country code
  const patientTzLabel = patientTimezone?.split('/').pop()?.replace(/_/g, ' ');
  const hasDifferentTz = patientTimezone && patientTimezone !== therapistTimezone;
  const patientIso2 = patientTimezone ? timezoneToCountryCode(patientTimezone) : null;

  // Filter slots by selected date & compute display data
  const displaySlots = useMemo(() => {
    if (!selectedDate) return [];
    const selectedDateStr = selectedDate.toLocaleDateString('en-CA');

    return slots
      .filter(s => {
        const d = new Date(s.slot);
        return d.toLocaleDateString('en-CA', { timeZone: therapistTimezone }) === selectedDateStr;
      })
      .map(s => {
        const d = new Date(s.slot);
        const endD = new Date(d.getTime() + sessionDuration * 60000);

        const therapistTime = d.toLocaleTimeString('es-AR', {
          timeZone: therapistTimezone, hour: '2-digit', minute: '2-digit', hour12: false,
        });
        const therapistEndTime = endD.toLocaleTimeString('es-AR', {
          timeZone: therapistTimezone, hour: '2-digit', minute: '2-digit', hour12: false,
        });

        let patientTime: string | undefined;
        let patientEndTime: string | undefined;
        if (hasDifferentTz) {
          try {
            const baseDateStr = d.toLocaleDateString('en-CA', { timeZone: therapistTimezone });
            const utcStart = localTzToUTC(baseDateStr, therapistTime, therapistTimezone);
            const utcEnd = new Date(utcStart.getTime() + sessionDuration * 60000);
            patientTime = isoToZoneParts(utcStart.toISOString(), patientTimezone!).time;
            patientEndTime = isoToZoneParts(utcEnd.toISOString(), patientTimezone!).time;
          } catch { /* noop */ }
        }

        return { id: s.id, therapistTime, therapistEndTime, patientTime, patientEndTime };
      });
  }, [slots, selectedDate, therapistTimezone, patientTimezone, hasDifferentTz, sessionDuration]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center">
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground m-0">Reprogramar sesión</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-muted transition-colors" aria-label="Cerrar">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left: session info + calendar */}
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-therapy-lavender-light border border-primary/20">
                <h3 className="text-sm font-semibold text-primary mb-2">Sesión actual</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-foreground">
                    <CalendarDays className="w-4 h-4 text-primary" />
                    <span>Fecha: <strong>{currentSession.date}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-foreground">
                    <User className="w-4 h-4 text-primary" />
                    <span>Paciente: <strong>{currentSession.patient}</strong></span>
                  </div>
                </div>
              </div>

              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateChange}
                disabled={(date: Date) => !availableDates.some(d => d.toDateString() === date.toDateString())}
                defaultMonth={availableDates[0]}
                locale={es}
                showOutsideDays={false}
                className="rounded-xl border border-border p-3 bg-card"
              />
            </div>

            {/* Right: time slots filtered by selected date */}
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">Horarios disponibles</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedDate
                    ? selectedDate.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                    : 'Seleccioná una fecha en el calendario'}
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-5 h-5 border-2 border-transparent border-t-primary rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Cargando horarios...</span>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              ) : displaySlots.length > 0 ? (
                <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                  {displaySlots.map(slot => {
                    const isSelected = selectedSlotId === slot.id;
                    return (
                      <button
                        key={slot.id}
                        onClick={() => onSelectSlot(slot.id)}
                        className={cn(
                          'w-full text-left px-4 py-3 rounded-xl transition-all duration-200 border-2',
                          isSelected
                            ? 'bg-primary/10 border-primary shadow-sm'
                            : 'bg-card border-border hover:border-primary/50 hover:bg-primary/5'
                        )}
                      >
                        {/* Therapist time */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className={cn('w-4 h-4 flex-shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                            <span className={cn('text-sm font-semibold', isSelected ? 'text-primary' : 'text-foreground')}>
                              {slot.therapistTime} - {slot.therapistEndTime}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            ({therapistIso2 && <><CountryFlag iso2={therapistIso2} size="0.75rem" /> </>}{therapistCountry})
                          </span>
                        </div>
                        {/* Patient time (only if different timezone) */}
                        {slot.patientTime && (
                          <div className="flex items-center justify-between mt-1.5 pl-6">
                            <div className="flex items-center gap-1.5">
                              {patientCountryCode && (
                                <CountryFlag iso2={patientCountryCode} size="0.875rem" />
                              )}
                              <span className="text-xs text-muted-foreground">
                                {slot.patientTime} - {slot.patientEndTime}
                              </span>
                            </div>
                            {patientTzLabel && (
                              <span className="text-xs text-muted-foreground">
                                ({patientIso2 && <><CountryFlag iso2={patientIso2} size="0.75rem" /> </>}{patientTzLabel})
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : selectedDate ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No hay horarios disponibles para esta fecha
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="bg-primary hover:bg-primary/90"
            disabled={!selectedSlotId || confirming}
            onClick={onConfirm}
          >
            {confirming ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-transparent border-t-white rounded-full animate-spin mr-1" />
                Reprogramando...
              </>
            ) : (
              'Confirmar'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default RescheduleModal;
